// Página AdminSubcategories - Gestión de subcategorías de productos
// Permite crear, editar y eliminar subcategorías asociadas a categorías

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSubcategories, useSubcategoryActions } from '../../hooks/useCategories'
import { useCategories } from '../../hooks/useCategories'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './AdminSubcategories.css'

/**
 * Página AdminSubcategories
 * Gestión completa de subcategorías (CRUD)
 */
function AdminSubcategories() {
  const { categories } = useCategories(true) // Todas las categorías
  const { subcategories, loading, error, refresh } = useSubcategories(null, true) // Todas las subcategorías
  const { create, update, remove, loading: actionLoading } = useSubcategoryActions()
  
  const [showForm, setShowForm] = useState(false)
  const [editingSubcategory, setEditingSubcategory] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    description: '',
    active: true
  })
  const [formErrors, setFormErrors] = useState({})
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [notification, setNotification] = useState(null)

  // Obtener nombre de categoría por ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId)
    return category ? category.name : 'Sin categoría'
  }

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  // Validar formulario
  const validate = () => {
    const errors = {}
    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido'
    }
    if (!formData.categoryId) {
      errors.categoryId = 'Debes seleccionar una categoría'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Guardar subcategoría (crear o actualizar)
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    try {
      if (editingSubcategory) {
        await update(editingSubcategory.id, formData)
        setNotification({
          type: 'success',
          message: 'Subcategoría actualizada correctamente'
        })
      } else {
        await create(formData)
        setNotification({
          type: 'success',
          message: 'Subcategoría creada correctamente'
        })
      }
      resetForm()
      refresh()
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Editar subcategoría
  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory)
    setFormData({
      name: subcategory.name || '',
      categoryId: subcategory.categoryId || '',
      description: subcategory.description || '',
      active: subcategory.active !== false
    })
    setShowForm(true)
  }

  // Eliminar subcategoría
  const handleDelete = async (id) => {
    try {
      await remove(id)
      setNotification({
        type: 'success',
        message: 'Subcategoría eliminada correctamente'
      })
      setDeleteConfirm(null)
      refresh()
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Error: ${error.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Resetear formulario
  const resetForm = () => {
    setFormData({ name: '', categoryId: '', description: '', active: true })
    setEditingSubcategory(null)
    setShowForm(false)
    setFormErrors({})
  }

  if (loading) {
    return <div className="loading-state">Cargando subcategorías...</div>
  }

  if (error) {
    return <div className="error-state">Error: {error}</div>
  }

  return (
    <div className="admin-subcategories-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />
      <div className="subcategories-header">
        <div>
          <h2>Gestión de Subcategorías</h2>
          <p>Administra las subcategorías asociadas a cada categoría</p>
        </div>
        <div className="header-actions">
          <Link to="/admin/configuraciones">
            <Button variant="outline">← Volver</Button>
          </Link>
          <Button 
            variant="primary" 
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancelar' : '+ Nueva Subcategoría'}
          </Button>
        </div>
      </div>

      {/* Formulario de subcategoría */}
      {showForm && (
        <div className="subcategory-form-section">
          <h3>{editingSubcategory ? 'Editar Subcategoría' : 'Nueva Subcategoría'}</h3>
          <form onSubmit={handleSubmit} className="subcategory-form">
            <div className="form-group">
              <label htmlFor="categoryId">
                Categoría <span className="required">*</span>
              </label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className={formErrors.categoryId ? 'error' : ''}
                disabled={actionLoading}
              >
                <option value="">Seleccionar categoría</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {formErrors.categoryId && <span className="error-message">{formErrors.categoryId}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="name">
                Nombre de la Subcategoría <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ej: Sofás de 3 cuerpos"
                className={formErrors.name ? 'error' : ''}
                disabled={actionLoading}
              />
              {formErrors.name && <span className="error-message">{formErrors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Descripción opcional de la subcategoría..."
                rows="3"
                disabled={actionLoading}
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  disabled={actionLoading}
                />
                <span>Subcategoría activa (visible en el catálogo)</span>
              </label>
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={resetForm} disabled={actionLoading}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={actionLoading}>
                {actionLoading 
                  ? (editingSubcategory ? 'Guardando...' : 'Creando...') 
                  : (editingSubcategory ? 'Guardar Cambios' : 'Crear Subcategoría')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de subcategorías */}
      <div className="subcategories-list">
        <h3>Subcategorías Existentes ({subcategories.length})</h3>
        {subcategories.length === 0 ? (
          <div className="empty-state">
            <p>No hay subcategorías creadas. Crea la primera subcategoría para comenzar.</p>
          </div>
        ) : (
          <>
            {/* Tabla para desktop */}
            <div className="subcategories-table-container desktop-view">
              <table className="subcategories-table">
                <thead>
                  <tr>
                    <th>Categoría</th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {subcategories.map((subcategory) => (
                    <tr key={subcategory.id}>
                      <td>
                        <span className="category-badge">
                          {getCategoryName(subcategory.categoryId)}
                        </span>
                      </td>
                      <td>
                        <strong>{subcategory.name}</strong>
                        <br />
                        <small className="slug-text">/{subcategory.slug || 'sin-slug'}</small>
                      </td>
                      <td>{subcategory.description || '-'}</td>
                      <td>
                        <span className={`status-badge ${subcategory.active ? 'active' : 'inactive'}`}>
                          {subcategory.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => handleEdit(subcategory)}
                            className="action-btn edit-btn"
                            title="Editar"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(subcategory.id)}
                            className="action-btn delete-btn"
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards para móviles */}
            <div className="subcategory-card-grid mobile-view">
              {subcategories.map((subcategory) => (
                <div key={subcategory.id} className="subcategory-card">
                  <div className="card-header">
                    <div>
                      <h4 className="card-subcategory-name">{subcategory.name}</h4>
                      <small className="slug-text">/{subcategory.slug || 'sin-slug'}</small>
                    </div>
                    <span className={`status-badge ${subcategory.active ? 'active' : 'inactive'}`}>
                      {subcategory.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="card-info-row">
                      <span className="info-label">Categoría:</span>
                      <span className="category-badge">
                        {getCategoryName(subcategory.categoryId)}
                      </span>
                    </div>
                    {subcategory.description && (
                      <div className="card-info-row">
                        <span className="info-label">Descripción:</span>
                        <span className="info-value">{subcategory.description}</span>
                      </div>
                    )}
                  </div>
                  <div className="card-actions">
                    <button
                      onClick={() => handleEdit(subcategory)}
                      className="action-btn edit-btn"
                      title="Editar"
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(subcategory.id)}
                      className="action-btn delete-btn"
                      title="Eliminar"
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>¿Eliminar subcategoría?</h3>
            <p>Esta acción no se puede deshacer. ¿Estás seguro?</p>
            <div className="modal-actions">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={() => handleDelete(deleteConfirm)}>
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSubcategories

